/**
 * @file ShipmentEventRow.tsx
 * @description Одно событие ленты доставки (phase19.13 шаг 6; типографика и
 * раскладка — phase19.24 шаг 5).
 *
 * На < md — карточка с маркером-таймлайном (разметка та же, что у
 * `OrderTimeline`), на ≥ md — строка грида `[время][событие][источник]`.
 *
 * Технический код перевозчика больше не занимает свою колонку: он идёт второй
 * строкой под названием статуса. Причина — дефект 1280px: колонка кода
 * фиксированной ширины налезала на бейдж источника («вручную · Администратор
 * Chaiberi»). У ручных отметок кода нет смысла вовсе — его придумал не
 * перевозчик, а мы сами, поэтому для `source === "MANUAL"` он не показывается.
 *
 * Этапы и источники — реестрами: новый этап у перевозчика = запись в
 * `STAGE_MARKER`, без правок разметки.
 */

import {
  Bike,
  Braces,
  CheckCircle2,
  FileText,
  Hand,
  MapPin,
  PackageCheck,
  RefreshCw,
  Store,
  Truck,
  Undo2,
  XCircle,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import {
  DELIVERY_STAGE_LABEL,
  SHIPMENT_EVENT_SOURCE_LABEL,
  type DeliveryStage,
  type ShipmentEventDto,
  type ShipmentEventSource,
} from "../../lib/api-delivery-monitoring";
import { hasRawPayload } from "../../lib/shipment-events-view";
import { formatShopTime } from "../../lib/shop-time";

/** Иконка и тон маркера по этапу. */
const STAGE_MARKER: Record<DeliveryStage, { icon: LucideIcon; tone: string }> = {
  CREATED: { icon: FileText, tone: "text-on-surface-variant" },
  ACCEPTED: { icon: PackageCheck, tone: "text-on-surface-variant" },
  IN_TRANSIT: { icon: Truck, tone: "text-on-surface-variant" },
  ARRIVED_DESTINATION: { icon: MapPin, tone: "text-on-surface-variant" },
  READY_FOR_PICKUP: { icon: Store, tone: "text-primary" },
  OUT_FOR_DELIVERY: { icon: Bike, tone: "text-primary" },
  DELIVERED: { icon: CheckCircle2, tone: "text-primary" },
  NOT_DELIVERED: { icon: XCircle, tone: "text-destructive" },
  RETURNED: { icon: Undo2, tone: "text-destructive" },
};

/** Иконка источника события. */
const SOURCE_ICON: Record<ShipmentEventSource, LucideIcon> = {
  WEBHOOK: Zap,
  SYNC: RefreshCw,
  MANUAL: Hand,
};

interface Props {
  event: ShipmentEventDto;
  /** < md — карточный вид с маркером. */
  compact: boolean;
  /** Открыть сырой payload перевозчика; кнопки нет, если `raw` не пришёл. */
  onShowRaw: (event: ShipmentEventDto) => void;
}

export function ShipmentEventRow({ event, compact, onShowRaw }: Props) {
  const notify = useNotify();
  const marker = STAGE_MARKER[event.stage];
  const MarkerIcon = marker.icon;
  const SourceIcon = SOURCE_ICON[event.source];
  const sourceLabel =
    event.source === "MANUAL" && event.actorName
      ? `${SHIPMENT_EVENT_SOURCE_LABEL.MANUAL} · ${event.actorName}`
      : SHIPMENT_EVENT_SOURCE_LABEL[event.source];
  // Код ручной отметки ставим мы сами — менеджеру он ничего не объясняет.
  const showCode = event.source !== "MANUAL" && !!event.carrierCode;

  async function copyCode(): Promise<void> {
    try {
      await navigator.clipboard.writeText(event.carrierCode);
      notify.success(`Код скопирован: ${event.carrierCode}`);
    } catch {
      notify.error("Браузер не дал скопировать — выделите код вручную");
    }
  }

  return (
    <li
      className={
        compact
          ? "relative"
          : // Колонка источника — по содержимому: бейдж «вручную · Иванов И.»
            // длиннее фиксированных 110px и налезал на соседей (QA 1280px).
            "grid grid-cols-[120px_minmax(0,1fr)_max-content] items-start gap-3 py-2"
      }
    >
      {compact ? (
        <span
          aria-hidden
          className={`absolute -left-8 top-0 flex size-6 items-center justify-center rounded-full bg-surface-lowest ${marker.tone}`}
        >
          <MarkerIcon className="size-4" />
        </span>
      ) : null}

      <span className="text-xs tabular-nums text-on-surface-variant">
        {formatShopTime(event.occurredAt)}
      </span>

      <div className="min-w-0">
        <span className="text-sm font-medium">{DELIVERY_STAGE_LABEL[event.stage]}</span>
        {event.city ? (
          <span className="ml-1.5 text-sm text-on-surface-variant">{event.city}</span>
        ) : null}
        {event.comment ? (
          <p className="mt-0.5 break-words text-xs text-on-surface-variant">
            {event.comment}
          </p>
        ) : null}
        {showCode ? (
          <button
            type="button"
            onClick={() => void copyCode()}
            title="Скопировать код перевозчика"
            // Тач-цель 48px на телефоне, компактная строка на десктопе.
            className="mt-0.5 flex min-h-12 w-full min-w-0 items-center rounded text-left font-mono text-xs text-on-surface-variant hover:text-on-surface focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none lg:min-h-6"
          >
            {/*
              Обрезает многоточием именно span: у флекс-контейнера `truncate`
              не действует на анонимный текст, и длинный код перевозчика
              (СДЭК шлёт 30+ символов) распирал колонку на 360px.
            */}
            <span className="truncate">{event.carrierCode}</span>
          </button>
        ) : null}
      </div>

      <div
        className={
          compact
            ? "mt-1 flex flex-wrap items-center gap-2"
            : "flex items-center justify-end gap-1"
        }
      >
        <SourceBadge icon={SourceIcon} label={sourceLabel} />
        {hasRawPayload(event) ? <RawButton onClick={() => onShowRaw(event)} /> : null}
      </div>
    </li>
  );
}

function SourceBadge({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <Badge variant="outline" className="shrink-0">
      <Icon />
      {label}
    </Badge>
  );
}

function RawButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="icon-sm"
      variant="ghost"
      onClick={onClick}
      title="Сырой ответ перевозчика"
      aria-label="Показать сырой ответ перевозчика"
    >
      <Braces className="size-4" />
    </Button>
  );
}
